import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import type { ConnectionStatus } from '../../hooks/use-sse-stream.ts'
import { Routes } from '../../routes.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './freeform-upload-activity.css.ts'
import {
  type FreeformStatusPhase,
  type FreeformUploadEvent,
  useFreeformUploadEvents,
} from './use-freeform-upload-events.ts'

type RepoScan = {
  repoFullName: string
  scanId: string
  findingCount: number
  lowestConfidence?: number
  weakestRationale?: string
}

// Association is a judgment the backend agent makes, not a string match, so a scan can land on the
// wrong repository. Below this the UI says so: the agent's own confidence is the only signal a user
// has that a scan is worth double-checking before acting on its fixes.
const LOW_CONFIDENCE_THRESHOLD = 0.6

export type FreeformProgress = {
  phase: FreeformStatusPhase
  detail?: string
  files: { filename: string; parseStatus: string }[]
  repoScans: RepoScan[]
  unassociatedCount: number
  isDone: boolean
  errorReason?: string
}

// Fold the replayed SSE event list (§B8) into a single render-ready summary. The events endpoint
// replays full history on every connection, so deriving from the whole list (rather than mutating on
// each event) stays correct across reconnects.
export const deriveFreeformProgress = (events: FreeformUploadEvent[]): FreeformProgress => {
  let phase: FreeformStatusPhase = 'parsing'
  let detail: string | undefined
  const filesByName = new Map<string, string>()
  const scansById = new Map<string, RepoScan>()
  let unassociatedCount = 0
  let isDone = false
  let errorReason: string | undefined

  for (const event of events) {
    match(event)
      .with({ type: 'status' }, statusEvent => {
        phase = statusEvent.phase
        detail = statusEvent.detail
      })
      .with({ type: 'file' }, fileEvent => {
        filesByName.set(fileEvent.filename, fileEvent.parseStatus)
      })
      .with({ type: 'repo_scan' }, scanEvent => {
        scansById.set(scanEvent.scanId, {
          repoFullName: scanEvent.repoFullName,
          scanId: scanEvent.scanId,
          findingCount: scanEvent.findingCount,
          lowestConfidence: scanEvent.lowestConfidence,
          weakestRationale: scanEvent.weakestRationale,
        })
      })
      .with({ type: 'unassociated' }, unassociatedEvent => {
        unassociatedCount = unassociatedEvent.count
      })
      .with({ type: 'done' }, () => {
        isDone = true
      })
      .with({ type: 'error' }, errorEvent => {
        isDone = true
        errorReason = errorEvent.reason
        phase = 'failed'
      })
      .exhaustive()
  }

  return {
    phase,
    detail,
    files: [...filesByName.entries()].map(([filename, parseStatus]) => ({ filename, parseStatus })),
    repoScans: [...scansById.values()],
    unassociatedCount,
    isDone,
    errorReason,
  }
}

const getPhaseLabel = (phase: FreeformStatusPhase): string =>
  match(phase)
    .with('parsing', () => 'Parsing your upload…')
    .with('associating', () => 'Searching your repositories to place each finding…')
    .with('scanning', () => 'Creating scans…')
    .with('completed', () => 'Analysis started')
    .with('failed', () => 'Upload failed')
    .exhaustive()

// Surfaces the weakest association behind a scan. The rationale rides along as the tooltip rather
// than inline text: it is a sentence of prose, and the row is a list item, not a report.
const LowConfidenceChip: React.FC<{ scan: RepoScan }> = ({ scan }) => {
  const { lowestConfidence, weakestRationale } = scan
  const isLowConfidence = lowestConfidence !== undefined && lowestConfidence < LOW_CONFIDENCE_THRESHOLD
  if (!isLowConfidence) {
    return undefined
  }

  const rationale = weakestRationale ?? 'Pixee was not confident this finding belongs to this repository.'

  return (
    <TooltipWithHover
      trigger={
        <span className={styles.lowConfidenceChip} tabIndex={0}>
          <WarningCircleIcon size={12} weight="fill" aria-hidden />
          Low confidence
        </span>
      }
    >
      {rationale}
    </TooltipWithHover>
  )
}

export type FreeformActivityViewProps = {
  progress: FreeformProgress
  connectionStatus: ConnectionStatus
}

export const FreeformActivityView: React.FC<FreeformActivityViewProps> = ({ progress, connectionStatus }) => {
  const { phase, detail, files, repoScans, unassociatedCount, isDone, errorReason } = progress
  const isFailed = phase === 'failed' || errorReason !== undefined
  const isComplete = isDone && !isFailed
  const isStreamError = connectionStatus === 'error'

  return (
    <section className={styles.card} aria-label="Findings intake activity">
      <header className={styles.header}>
        {match({ isFailed, isComplete })
          .with({ isFailed: true }, () => (
            <WarningCircleIcon size={18} weight="fill" className={styles.failedIcon} aria-hidden />
          ))
          .with({ isComplete: true }, () => (
            <CheckCircleIcon size={18} weight="fill" className={styles.readyIcon} aria-hidden />
          ))
          .otherwise(() => (
            <span className={styles.spinner} aria-hidden />
          ))}
        <div>
          <p className={styles.headerTitle}>{getPhaseLabel(phase)}</p>
          {detail !== undefined ? <p className={styles.headerDetail}>{detail}</p> : null}
        </div>
      </header>

      {isFailed ? (
        <div className={styles.section}>
          <p className={styles.errorText}>
            {errorReason ?? 'Something went wrong while processing this upload. Please try again.'}
          </p>
        </div>
      ) : null}

      {files.length > 0 ? (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Files</p>
          <ul className={styles.list}>
            {files.map(file => (
              <li key={file.filename} className={styles.row}>
                <span className={styles.rowName} title={file.filename}>
                  {file.filename}
                </span>
                <span className={styles.rowMeta}>{file.parseStatus}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.section}>
        <p className={styles.sectionTitle}>Scans created</p>
        {repoScans.length > 0 ? (
          <ul className={styles.list}>
            {repoScans.map(scan => (
              <li key={scan.scanId} className={styles.row}>
                <span className={styles.rowName} title={scan.repoFullName}>
                  {scan.repoFullName}
                </span>
                <span className={styles.rowMeta}>
                  {scan.findingCount} {pluralize(scan.findingCount, 'finding', 'findings')}
                </span>
                <LowConfidenceChip scan={scan} />
                <Link className={styles.scanLink} to={Routes.ScanDetailsPage.createPath({ scanId: scan.scanId })}>
                  View scan
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyNote}>No scans created yet.</p>
        )}
      </div>

      {unassociatedCount > 0 ? (
        <div className={styles.section}>
          <p className={styles.unassociatedText}>
            <WarningCircleIcon size={14} weight="fill" aria-hidden />
            {unassociatedCount} {pluralize(unassociatedCount, 'finding', 'findings')} could not be matched to a
            repository and {pluralize(unassociatedCount, 'was', 'were')} logged for review.
          </p>
        </div>
      ) : null}

      {isStreamError && !isDone ? (
        <div className={styles.section}>
          <p className={styles.emptyNote}>
            Lost connection to the progress stream. Processing continues in the background.
          </p>
        </div>
      ) : null}
    </section>
  )
}

type FreeformUploadActivityProps = {
  organizationId?: string
  sessionId: string
}

export const FreeformUploadActivity: React.FC<FreeformUploadActivityProps> = ({ organizationId, sessionId }) => {
  const { events, connectionStatus } = useFreeformUploadEvents({ organizationId, sessionId })
  const progress = deriveFreeformProgress(events)
  return <FreeformActivityView progress={progress} connectionStatus={connectionStatus} />
}
